/**
 * Claude AI Service
 * Provides AI-powered features throughout the platform
 * 
 * SECURITY UPDATE: Now routes requests through Firebase Cloud Functions
 * to protect API keys and enforce rate limits.
 */

import { functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';

// Define the response type from the backend
interface AIRouteResponse {
  success: boolean;
  model: string;
  content: string;
  tokensUsed?: {
    input: number;
    output: number;
  };
}

// Initialize callable function
const aiRoute = httpsCallable<any, AIRouteResponse>(functions, 'aiRoute');

/**
 * Helper to call the AI backend with a specific system prompt
 */
async function callAI(
  task: string,
  content: string,
  systemPrompt?: string,
  contextData: any = {}
): Promise<string> {
  try {
    const result = await aiRoute({
      task: 'legal_drafting', // Default to drafting/reasoning tasks for Claude
      content,
      options: {
        temperature: 0.7,
        maxTokens: 4096,
      },
      context: {
        systemPrompt,
        ...contextData
      }
    });

    return result.data.content;
  } catch (error) {
    console.error('AI Service Error:', error);
    throw new Error('Failed to process AI request. Please try again later.');
  }
}

// ============================================
// TEMPLATE ASSISTANCE
// ============================================

export interface TemplateGenerationRequest {
  documentType: string;
  jurisdiction: string;
  description: string;
  includeVariables?: string[];
}

export interface TemplateGenerationResponse {
  content: string;
  suggestedVariables: Array<{
    name: string;
    type: string;
    description: string;
    required: boolean;
  }>;
  explanation: string;
}

/**
 * Generate a new template from a description
 */
export async function generateTemplate(
  request: TemplateGenerationRequest
): Promise<TemplateGenerationResponse> {
  const systemPrompt = `You are an expert legal document automation specialist. You create templates using a custom DSL with the following syntax:

DOCUMENT "Name"
DESCRIPTION "Description"
VERSION "1.0"

SECTION "Section Name"
  Content with {{variables}} and {{variable | filter}}
END

Available features:
- Variables: {{variable.name}}
- Filters: {{value | uppercase}}, {{date | date: 'long'}}, {{amount | currency}}, {{name | possessive}}, {{count | ordinal}}, {{items | pluralize: 'item'}}
- Conditionals: {{#if condition}}...{{/if}}, {{#unless condition}}...{{/unless}}
- Loops: {{#each items as item}}...{{/each}}
- Nested: {{client.address.street}}

Always follow legal document conventions for the specified jurisdiction.
Use clear variable names like grantor.name, grantee.address, property.legal_description.
Include proper signature blocks and notary sections where appropriate.`;

  const userContent = `Create a ${request.documentType} template for ${request.jurisdiction}.

Description: ${request.description}

${request.includeVariables?.length ? `Include these variables: ${request.includeVariables.join(', ')}` : ''}

Respond with JSON in this exact format:
{
  "content": "The full DSL template content",
  "suggestedVariables": [
    {"name": "variable.name", "type": "text|date|number|boolean|array", "description": "What this variable is for", "required": true}
  ],
  "explanation": "Brief explanation of the template structure and key sections"
}`;

  const text = await callAI('legal_drafting', userContent, systemPrompt, { jurisdiction: request.jurisdiction });

  // Parse JSON from response
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Failed to parse AI response');
  }

  return JSON.parse(jsonMatch[0]);
}

/**
 * Improve an existing template
 */
export async function improveTemplate(
  currentContent: string,
  instructions: string
): Promise<{ improvedContent: string; changes: string[] }> {
  const systemPrompt = `You are an expert legal document automation specialist. Improve templates while maintaining the DSL syntax.`;

  const userContent = `Improve this template based on the instructions.

Current template:
\`\`\`
${currentContent}
\`\`\`

Instructions: ${instructions}

Respond with JSON:
{
  "improvedContent": "The improved DSL template",
  "changes": ["List of changes made"]
}`;

  const text = await callAI('legal_drafting', userContent, systemPrompt);

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Failed to parse AI response');
  }

  return JSON.parse(jsonMatch[0]);
}

// ============================================
// CLAUSE SUGGESTIONS
// ============================================

export interface ClauseSuggestion {
  name: string;
  content: string;
  category: string;
  explanation: string;
  relevanceScore: number;
}

/**
 * Get clause suggestions based on document context
 */
export async function suggestClauses(
  documentType: string,
  jurisdiction: string,
  existingClauses: string[],
  context?: string
): Promise<ClauseSuggestion[]> {
  const systemPrompt = `You are a legal expert specializing in ${jurisdiction} law. Suggest relevant clauses for legal documents.`;

  const userContent = `Suggest clauses for a ${documentType} in ${jurisdiction}.

Already included: ${existingClauses.join(', ') || 'None'}
${context ? `Additional context: ${context}` : ''}

Respond with JSON array:
[
  {
    "name": "Clause Name",
    "content": "The clause text with {{variables}} if needed",
    "category": "warranties|boilerplate|signatures|definitions|provisions",
    "explanation": "Why this clause is important",
    "relevanceScore": 0.95
  }
]

Suggest 3-5 relevant clauses not already included.`;

  const text = await callAI('clause_analysis', userContent, systemPrompt, { jurisdiction });

  const jsonMatch = text.match(/\[[\s\S]*\]/);
  if (!jsonMatch) {
    return [];
  }

  return JSON.parse(jsonMatch[0]);
}

// ============================================
// DOCUMENT ANALYSIS
// ============================================

export interface DocumentAnalysis {
  summary: string;
  keyTerms: Array<{ term: string; definition: string }>;
  parties: Array<{ role: string; name: string }>;
  obligations: Array<{ party: string; obligation: string }>;
  dates: Array<{ description: string; date: string }>;
  risks: Array<{ risk: string; severity: 'low' | 'medium' | 'high'; recommendation: string }>;
  missingElements: string[];
}

/**
 * Analyze a generated document
 */
export async function analyzeDocument(
  documentContent: string,
  documentType: string
): Promise<DocumentAnalysis> {
  const systemPrompt = `You are a legal document analyst. Provide thorough, accurate analysis of legal documents.`;

  const userContent = `Analyze this ${documentType}:

${documentContent}

Respond with JSON:
{
  "summary": "2-3 sentence summary of the document",
  "keyTerms": [{"term": "Term", "definition": "What it means in this context"}],
  "parties": [{"role": "Grantor/Grantee/etc", "name": "Party name from document"}],
  "obligations": [{"party": "Who", "obligation": "What they must do"}],
  "dates": [{"description": "What the date is for", "date": "The date"}],
  "risks": [{"risk": "Potential issue", "severity": "low|medium|high", "recommendation": "How to address"}],
  "missingElements": ["Things that might be missing from this document type"]
}`;

  const text = await callAI('strategic_reasoning', userContent, systemPrompt);

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Failed to parse AI response');
  }

  return JSON.parse(jsonMatch[0]);
}

// ============================================
// VARIABLE EXTRACTION
// ============================================

export interface ExtractedVariables {
  variables: Record<string, any>;
  confidence: number;
  clarifications: string[];
}

/**
 * Extract variables from natural language description
 */
export async function extractVariablesFromText(
  userInput: string,
  templateVariables: Array<{ name: string; type: string; required: boolean }>
): Promise<ExtractedVariables> {
  const variablesList = templateVariables
    .map(v => `- ${v.name} (${v.type}, ${v.required ? 'required' : 'optional'})`)
    .join('\n');

  const systemPrompt = `You extract structured data from natural language for legal documents. Be precise and ask for clarification when information is ambiguous.`;

  const userContent = `Extract variable values from this description:

"${userInput}"

Template variables needed:
${variablesList}

Respond with JSON:
{
  "variables": {
    "variable.name": "extracted value"
  },
  "confidence": 0.85,
  "clarifications": ["Questions about unclear information"]
}`;

  const text = await callAI('strategic_reasoning', userContent, systemPrompt);

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Failed to parse AI response');
  }

  return JSON.parse(jsonMatch[0]);
}

// ============================================
// CHAT ASSISTANT
// ============================================

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatContext {
  currentPage?: string;
  documentType?: string;
  clientName?: string;
  matterName?: string;
}

/**
 * AI Chat assistant for the platform
 */
export async function chat(
  messages: ChatMessage[],
  context?: ChatContext
): Promise<string> {
  const contextInfo = context
    ? `Current context:
- Page: ${context.currentPage || 'Unknown'}
- Document Type: ${context.documentType || 'N/A'}
- Client: ${context.clientName || 'N/A'}
- Matter: ${context.matterName || 'N/A'}`
    : '';

  const systemPrompt = `You are a helpful AI assistant for a legal practice management platform called Lawity. You help attorneys and staff with:
- Creating and editing document templates
- Understanding the DSL syntax
- Managing clients and matters
- Generating documents
- E-signature workflows

${contextInfo}

Be concise, professional, and helpful. If you don't know something specific about this platform, say so.`;

  // We only send the last user message as content, but provide history in context
  const lastUserMessage = messages.filter(m => m.role === 'user').pop()?.content || '';
  const conversationHistory = messages.slice(0, -1); // All but last message

  const text = await callAI(
    'strategic_reasoning',
    lastUserMessage,
    systemPrompt,
    { conversationHistory }
  );

  return text;
}

// ============================================
// SMART SEARCH
// ============================================

export interface SearchResult {
  type: 'template' | 'clause' | 'client' | 'matter' | 'document';
  id: string;
  name: string;
  relevance: number;
  snippet: string;
}

/**
 * AI-powered semantic search across the platform
 */
export async function smartSearch(
  query: string,
  items: Array<{ type: string; id: string; name: string; content: string }>
): Promise<SearchResult[]> {
  // For small datasets, use Claude to rank relevance
  if (items.length === 0) return [];

  const itemsList = items
    .slice(0, 50) // Limit to prevent token overflow
    .map((item, i) => `[${i}] ${item.type}: "${item.name}" - ${item.content.slice(0, 200)}...`)
    .join('\n');

  const systemPrompt = `You are a search assistant. Rank items by relevance to the query.`;

  const userContent = `Query: "${query}"

Items:
${itemsList}

Return JSON array of relevant items (indices) with relevance scores (0-1):
[{"index": 0, "relevance": 0.95, "snippet": "Why this is relevant"}]

Only include items with relevance > 0.3. Max 10 results.`;

  const text = await callAI('needle_haystack', userContent, systemPrompt); // Use search-optimized task

  const jsonMatch = text.match(/\[[\s\S]*\]/);
  if (!jsonMatch) return [];

  const rankings = JSON.parse(jsonMatch[0]) as Array<{ index: number; relevance: number; snippet: string }>;

  return rankings
    .filter(r => r.index < items.length)
    .map(r => ({
      type: items[r.index].type as SearchResult['type'],
      id: items[r.index].id,
      name: items[r.index].name,
      relevance: r.relevance,
      snippet: r.snippet
    }))
    .sort((a, b) => b.relevance - a.relevance);
}

// ============================================
// PROOFREAD & SUGGESTIONS
// ============================================

export interface ProofreadResult {
  issues: Array<{
    type: 'grammar' | 'legal' | 'consistency' | 'clarity';
    location: string;
    issue: string;
    suggestion: string;
    severity: 'info' | 'warning' | 'error';
  }>;
  overallScore: number;
  suggestions: string[];
}

/**
 * Proofread generated document content
 */
export async function proofreadDocument(
  content: string,
  documentType: string,
  jurisdiction: string
): Promise<ProofreadResult> {
  const systemPrompt = `You are a legal proofreader specializing in ${jurisdiction} ${documentType} documents. Check for grammar, legal accuracy, consistency, and clarity.`;

  const userContent = `Proofread this ${documentType}:

${content}

Respond with JSON:
{
  "issues": [
    {
      "type": "grammar|legal|consistency|clarity",
      "location": "Section or paragraph reference",
      "issue": "What's wrong",
      "suggestion": "How to fix it",
      "severity": "info|warning|error"
    }
  ],
  "overallScore": 85,
  "suggestions": ["General improvement suggestions"]
}`;

  const text = await callAI('ethical_review', userContent, systemPrompt, { jurisdiction });

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    return { issues: [], overallScore: 100, suggestions: [] };
  }

  return JSON.parse(jsonMatch[0]);
}

// ============================================
// EXPORT
// ============================================

export const claudeAI = {
  generateTemplate,
  improveTemplate,
  suggestClauses,
  analyzeDocument,
  extractVariablesFromText,
  chat,
  smartSearch,
  proofreadDocument,
};

export default claudeAI;
